// audioPageLogger — CU-869dafa5q.
//
// Page-level diagnostic listeners that arm ONCE per page load and stay live
// for the lifetime of the document. Captures environmental signals that
// affect audio playout in backgrounded tabs but aren't tied to any single
// call: visibility transitions, page lifecycle, long tasks (main-thread
// blockage), media-device changes, network changes, and system clock skew.
//
// Per-call state lives in audioCallLogger; the worklet path's own lifecycle
// hooks live in backgroundedAudioWorklet. This file is the catch-all for
// signals that only make sense at the document level.
//
// Codex review (max-diagnostics pass) — each event below is documented
// in /tmp/codex-diag-max.md with priority + failure-mode tag. We're
// implementing Tier 1+2 (high-signal + cheap UA/page-level) in one push
// because the deploy cycle is >1 week; Tier 3 deferred.

import logger from '../services/logger';
import {bumpDiagCounter, isAudioCallActive, isAudioDiagEnabled} from './audioDiagCounters';

let armed = false;

// Visibility / page lifecycle transitions — these already have piecemeal
// loggers in backgroundedAudioWorklet, but those only fire when a worklet
// graph is attached. We want the transitions ALL the time so a call that
// chops without a worklet graph still has visibility context in logs.
let lastHiddenAtMs: number | null = null;
let lastVisibilityState: DocumentVisibilityState = 'visible';
let lastHasFocus = true;

function emitVisibilityTransition(source: 'visibilitychange' | 'focus' | 'blur'): void {
    const prevVisibilityState = lastVisibilityState;
    const visibilityState = document.visibilityState;
    const prevHasFocus = lastHasFocus;
    const hasFocus = document.hasFocus();
    const hiddenForMsBefore = lastHiddenAtMs != null && prevVisibilityState === 'hidden'
        ? Date.now() - lastHiddenAtMs
        : null;
    if (visibilityState === 'hidden' && prevVisibilityState !== 'hidden') {
        lastHiddenAtMs = Date.now();
    } else if (visibilityState === 'visible' && prevVisibilityState === 'hidden') {
        lastHiddenAtMs = null;
    }
    logger.info('Visibility transition', {
        source: 'sip', event: 'audio_visibility_transition',
        transitionSource: source,
        prevVisibilityState, visibilityState,
        prevHasFocus, hasFocus, hiddenForMsBefore,
        tsWallMs: Date.now(),
    });
    lastVisibilityState = visibilityState;
    lastHasFocus = hasFocus;
}

function armPageLifecycle(): void {
    const onPageHide = (e: PageTransitionEvent) => {
        logger.info('Page lifecycle: pagehide', {
            source: 'sip', event: 'audio_page_lifecycle_transition',
            transition: 'pagehide',
            persisted: e.persisted,
            visibilityState: document.visibilityState,
            tsWallMs: Date.now(),
        });
    };
    const onBeforeUnload = () => {
        logger.info('Page lifecycle: beforeunload', {
            source: 'sip', event: 'audio_page_lifecycle_transition',
            transition: 'beforeunload',
            visibilityState: document.visibilityState,
            tsWallMs: Date.now(),
        });
    };
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('beforeunload', onBeforeUnload);
}

function armVisibilityListeners(): void {
    document.addEventListener('visibilitychange', () => emitVisibilityTransition('visibilitychange'));
    window.addEventListener('focus', () => emitVisibilityTransition('focus'));
    window.addEventListener('blur', () => emitVisibilityTransition('blur'));
}

// Long tasks — `PerformanceObserver('longtask')` reports any task that
// blocked the main thread for >50 ms. Threshold-gated to keep log volume
// reasonable; 200 ms is well above normal React renders and likely to be
// causal in chop events.
const LONG_TASK_THRESHOLD_MS = 200;

interface LongTaskBucket {
    count: number;
    totalDurationMs: number;
    maxDurationMs: number;
    over200msCount: number;
    hiddenLongTaskCount: number;
    durations: number[];
}

let longTaskBucket: LongTaskBucket = freshLongTaskBucket();
function freshLongTaskBucket(): LongTaskBucket {
    return {
        count: 0, totalDurationMs: 0, maxDurationMs: 0,
        over200msCount: 0, hiddenLongTaskCount: 0, durations: [],
    };
}

function p95(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const idx = Math.floor(0.95 * (sorted.length - 1));
    return sorted[idx];
}

function armLongTaskObserver(): void {
    if (typeof PerformanceObserver === 'undefined') return;
    const supported = (PerformanceObserver as unknown as {supportedEntryTypes?: string[]}).supportedEntryTypes;
    if (supported && !supported.includes('longtask')) return;
    try {
        const observer = new PerformanceObserver((entries) => {
            entries.getEntries().forEach((entry) => {
                const duration = entry.duration;
                longTaskBucket.count++;
                longTaskBucket.totalDurationMs += duration;
                longTaskBucket.maxDurationMs = Math.max(longTaskBucket.maxDurationMs, duration);
                longTaskBucket.durations.push(duration);
                if (duration > 200) longTaskBucket.over200msCount++;
                if (document.hidden) longTaskBucket.hiddenLongTaskCount++;
                if (duration >= LONG_TASK_THRESHOLD_MS) {
                    bumpDiagCounter('longTaskOver200ms');
                    logger.warn('Long task', {
                        source: 'sip', event: 'audio_long_task',
                        durationMs: duration,
                        startTimeMs: entry.startTime,
                        entryType: entry.entryType,
                        name: entry.name,
                        visibilityState: document.visibilityState,
                        hasFocus: document.hasFocus(),
                    });
                }
            });
        });
        observer.observe({entryTypes: ['longtask']});
    } catch (err) {
        logger.info('Long task observer unavailable', {
            source: 'sip', event: 'audio_long_task_observer_failed',
            errorMessage: (err as Error)?.message,
        });
    }
    // 5s summary keeps a coarse log of main-thread health even when no
    // single task crossed the threshold.
    setInterval(() => {
        if (longTaskBucket.count === 0) return;
        const b = longTaskBucket;
        logger.info('Long task summary', {
            source: 'sip', event: 'audio_long_task_summary',
            windowMs: 5000,
            count: b.count,
            totalDurationMs: b.totalDurationMs,
            maxDurationMs: b.maxDurationMs,
            p95DurationMs: p95(b.durations),
            over200msCount: b.over200msCount,
            hiddenLongTaskCount: b.hiddenLongTaskCount,
            visibilityState: document.visibilityState,
        });
        longTaskBucket = freshLongTaskBucket();
    }, 5000);
}

// Media devices — device hot-plug can swap the default sink without us
// noticing, especially on Bluetooth disconnects. Logging the inventory
// gives us a baseline to compare against if a chop coincides with a
// device change.
function armMediaDevicesListener(): void {
    const md = navigator.mediaDevices;
    if (!md) return;
    const enumerate = async (trigger: 'init' | 'devicechange'): Promise<void> => {
        try {
            const devices = await md.enumerateDevices();
            const audioInputs = devices.filter(d => d.kind === 'audioinput');
            const audioOutputs = devices.filter(d => d.kind === 'audiooutput');
            const videoInputs = devices.filter(d => d.kind === 'videoinput');
            logger.info('Media devices change', {
                source: 'sip', event: 'audio_media_devices_change',
                trigger,
                audioInputCount: audioInputs.length,
                audioOutputCount: audioOutputs.length,
                videoInputCount: videoInputs.length,
                defaultAudioOutputPresent: audioOutputs.some(d => d.deviceId === 'default'),
                defaultAudioInputPresent: audioInputs.some(d => d.deviceId === 'default'),
                labelsAvailable: devices.some(d => d.label !== ''),
                tsWallMs: Date.now(),
            });
        } catch (err) {
            logger.warn('Media devices enumerate failed', {
                source: 'sip', event: 'audio_media_devices_enumerate_failed',
                trigger, errorMessage: (err as Error)?.message,
            });
        }
    };
    void enumerate('init');
    md.addEventListener?.('devicechange', () => {
        bumpDiagCounter('mediaDevicesChange');
        void enumerate('devicechange');
    });
}

// Network type / RTT changes — relevant if the chop coincides with a
// Wi-Fi→cellular handoff or similar.
function armNetworkListener(): void {
    const nav = navigator as Navigator & {
        connection?: NetworkInformation & EventTarget;
    };
    const conn = nav.connection;
    if (!conn) return;
    const snapshot = (trigger: 'init' | 'change') => {
        logger.info('Network change', {
            source: 'sip', event: 'audio_network_change',
            trigger,
            effectiveType: conn.effectiveType ?? null,
            downlinkMbps: conn.downlink ?? null,
            rttMs: conn.rtt ?? null,
            saveData: conn.saveData ?? null,
            type: (conn as NetworkInformation & {type?: string}).type ?? null,
            tsWallMs: Date.now(),
        });
    };
    snapshot('init');
    conn.addEventListener('change', () => {
        bumpDiagCounter('networkChange');
        snapshot('change');
    });
}

// System clock jump detector — runs every 10 s and watches the skew
// `Date.now() - performance.now()`. A jump can happen on suspend/resume,
// NTP correction, or hibernation. Threshold of 250 ms is well above
// normal noise.
const CLOCK_JUMP_THRESHOLD_MS = 250;

function armClockJumpDetector(): void {
    let prevSkewMs: number | null = null;
    let prevTimestampMs = Date.now();
    setInterval(() => {
        const now = Date.now();
        const skew = now - performance.now();
        const sinceLastSampleMs = now - prevTimestampMs;
        prevTimestampMs = now;
        if (prevSkewMs == null) {
            prevSkewMs = skew;
            return;
        }
        const skewDeltaMs = skew - prevSkewMs;
        if (Math.abs(skewDeltaMs) > CLOCK_JUMP_THRESHOLD_MS) {
            bumpDiagCounter('systemClockJump');
            logger.warn('System clock jump', {
                source: 'sip', event: 'audio_system_clock_jump',
                prevSkewMs, newSkewMs: skew, skewDeltaMs,
                thresholdMs: CLOCK_JUMP_THRESHOLD_MS,
                sinceLastSampleMs,
                direction: skewDeltaMs > 0 ? 'forward' : 'backward',
                visibilityState: document.visibilityState,
            });
        }
        prevSkewMs = skew;
    }, 10000);
}

// audio_memory_pressure — codex Tier 3 follow-up. `performance.memory` is
// non-standard, Chrome-only, and only present on cross-origin-isolated
// pages by default — but when it IS present, a chop coinciding with heap
// growth or limit pressure is a real failure class worth distinguishing.
// Logs every 10 s, also fires an explicit `thresholdCrossed` event at 80%
// utilisation.
interface PerformanceMemory {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
}
const MEMORY_PRESSURE_THRESHOLD_PCT = 80;
let memoryPrevUsed: number | null = null;
let memoryPrevCrossed = false;
function armMemoryPressureMonitor(): void {
    const mem = (performance as Performance & {memory?: PerformanceMemory}).memory;
    if (!mem) return;
    setInterval(() => {
        const current = (performance as Performance & {memory?: PerformanceMemory}).memory;
        if (!current) return;
        const heapUsedPct = (current.usedJSHeapSize / current.jsHeapSizeLimit) * 100;
        const crossed = heapUsedPct >= MEMORY_PRESSURE_THRESHOLD_PCT;
        const deltaUsedBytes = memoryPrevUsed != null ? current.usedJSHeapSize - memoryPrevUsed : null;
        const justCrossed = crossed && !memoryPrevCrossed;
        if (justCrossed) bumpDiagCounter('memoryPressureCrossed');
        // Codex review — sampler runs at page scope (no teardown), so
        // emit a full sample only when (a) a call is active or
        // (b) we crossed the threshold. Otherwise log nothing. Keeps
        // the threshold-crossed alert path useful without flooding
        // telemetry on idle sessions.
        if (isAudioCallActive() || justCrossed) {
            logger.info('Memory pressure sample', {
                source: 'sip', event: 'audio_memory_pressure',
                usedJSHeapSize: current.usedJSHeapSize,
                totalJSHeapSize: current.totalJSHeapSize,
                jsHeapSizeLimit: current.jsHeapSizeLimit,
                heapUsedPct,
                deltaUsedBytes,
                thresholdCrossed: justCrossed,
                callActive: isAudioCallActive(),
                visibilityState: document.visibilityState,
            });
        }
        memoryPrevUsed = current.usedJSHeapSize;
        memoryPrevCrossed = crossed;
    }, 10000);
}

// audio_user_activity_proxy — codex Tier 3 follow-up. Was the user
// actually interacting with the page during the chop? Debounced to one
// log per 30 s so we don't spam during normal typing. Activity inside a
// hidden tab is unusual and worth surfacing; activity at the moment of
// a chop hints at "user clicked, autoplay policy unblocked, audio resumed".
const USER_ACTIVITY_DEBOUNCE_MS = 30_000;
let lastUserActivityAtMs: number | null = null;
let lastUserActivityLogAtMs = 0;
function armUserActivityListeners(): void {
    const onActivity = (activityType: string) => () => {
        // Codex review — only log activity DURING an active call.
        // Listeners are page-scoped (no teardown), so the gate has to
        // be at emit time. Without it, this becomes a page-wide
        // interaction logger that records unrelated user behaviour.
        if (!isAudioCallActive()) return;
        const now = Date.now();
        const sinceLastActivityMs = lastUserActivityAtMs != null ? now - lastUserActivityAtMs : null;
        lastUserActivityAtMs = now;
        if (now - lastUserActivityLogAtMs < USER_ACTIVITY_DEBOUNCE_MS) return;
        lastUserActivityLogAtMs = now;
        logger.info('User activity proxy', {
            source: 'sip', event: 'audio_user_activity_proxy',
            activityType,
            visibilityState: document.visibilityState,
            hasFocus: document.hasFocus(),
            hiddenForMs: lastHiddenAtMs != null && document.visibilityState === 'hidden'
                ? now - lastHiddenAtMs : null,
            sinceLastActivityMs,
        });
    };
    window.addEventListener('pointerdown', onActivity('pointerdown'), {passive: true, capture: true});
    window.addEventListener('keydown', onActivity('keydown'), {passive: true, capture: true});
    window.addEventListener('touchstart', onActivity('touchstart'), {passive: true, capture: true});
}

/**
 * Idempotent — call once at app boot. Subsequent calls are no-ops.
 * No cleanup; these listeners are intended to live for the whole
 * page lifetime.
 */
export function armAudioPageLogger(): void {
    if (armed) return;
    // Kill switch — if diagnostics are off RIGHT NOW, don't register
    // anything and don't mark armed. That way a subsequent arm attempt
    // (made from startAudioCallLogger on each call setup) can install
    // the listeners once the operator re-enables diagnostics. Codex
    // review round 3 — without this, a page that booted with
    // ?diag=off would permanently miss the page-level diagnostics
    // even after the operator flipped phq_audio_diag back to 'on'.
    if (!isAudioDiagEnabled()) return;
    armed = true;
    try { armVisibilityListeners(); } catch { /* swallow */ }
    try { armPageLifecycle(); } catch { /* swallow */ }
    try { armLongTaskObserver(); } catch { /* swallow */ }
    try { armMediaDevicesListener(); } catch { /* swallow */ }
    try { armNetworkListener(); } catch { /* swallow */ }
    try { armClockJumpDetector(); } catch { /* swallow */ }
    try { armMemoryPressureMonitor(); } catch { /* swallow */ }
    try { armUserActivityListeners(); } catch { /* swallow */ }
    logger.info('Audio page logger armed', {
        source: 'sip', event: 'audio_page_logger_armed',
        tsWallMs: Date.now(),
    });
}

/**
 * Reset call-boundary-scoped state on the page-level loggers. Codex
 * review — without this, the user-activity debounce accumulates across
 * calls and suppresses real in-call signals. `memoryPrevCrossed` is
 * snapshot-aligned to the actual current heap (NOT reset to false) so
 * a call that starts while we're already above the threshold doesn't
 * generate a phantom "just crossed" event on the next sampler tick.
 * `memoryPrevUsed` is NOT touched — keeping the heap-size delta
 * meaningful on the first in-call sample.
 */
export function resetAudioPageLoggerCallScopedState(): void {
    const current = (performance as Performance & {memory?: PerformanceMemory}).memory;
    if (current) {
        const heapUsedPct = (current.usedJSHeapSize / current.jsHeapSizeLimit) * 100;
        memoryPrevCrossed = heapUsedPct >= MEMORY_PRESSURE_THRESHOLD_PCT;
    }
    lastUserActivityAtMs = null;
    lastUserActivityLogAtMs = 0;
}

// Arm at module load — module is imported transitively by sipService, which
// runs at app boot. Re-imports are no-ops.
armAudioPageLogger();

// Minimal type shims for non-stable APIs we touch through narrow checks.
interface NetworkInformation {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
}
